/**
 * 放送地点まるごとプレビュー (通し試聴) の「プレビュー条件」選択ダイアログ。
 *
 * - 「この放送地点を通しで試聴」(編集モーダルのフッター) を押すと、再生の前にこのダイアログを開き、
 *   サンプル条件 (遅延 / 線名 / 乗換駅 / 方面) を選んでもらう。
 *   「試聴を開始」で閉じて再生に進み、再生中の表示は `PointPreviewDialog` が引き継ぐ。
 * - 入力欄は個別プレビューと同じ共通部品 (`PreviewSamplePanel`) を使うため、
 *   遅延の分 ⇔ 秒 換算や入力欄の出し分けは個別プレビューと完全に一致する。
 * - 条件の状態 (`usePreviewSample`) は呼び出し側が持つ。ダイアログを閉じても入力値は残り、
 *   次に開いたときも同じ値が表示される。
 * - 見た目は `PointPreviewDialog` に揃える (既存の `.modal-backdrop` / `.modal` 系 CSS を流用)。
 * - 背景クリックでは閉じない。Esc でキャンセルする。
 * - z-index は編集モーダル / 地図ポップアップ (1100) より前面、`PointPreviewDialog` (1150) より背面の 1140。
 */
import { useEffect } from 'react';

import type {
  PreviewSampleContext,
  PreviewSampleFieldVisibility,
  PreviewSampleLimits,
  PreviewSampleLineContext,
} from './broadcastContents/types';

import { PreviewSamplePanel } from './broadcastContents/parts/PreviewSamplePanel';

export interface PointPreviewConditionDialogProps {
  /** 表示する入力欄の種類 (地点内のコンテンツから導出) */
  fields: PreviewSampleFieldVisibility;
  /** 現在のサンプル条件 (表示値) */
  sample: PreviewSampleContext;
  /** 指定できる入力枠 (接続線の件数 / 各接続線の方面欄数) */
  limits: PreviewSampleLimits;
  /** 1 項目の変更通知 */
  onChange: <K extends keyof PreviewSampleContext>(key: K, value: PreviewSampleContext[K]) => void;
  /** 接続線条件 (`lines[index]`) の 1 項目の変更通知 */
  onLineChange: <K extends keyof PreviewSampleLineContext>(
    index: number,
    key: K,
    value: PreviewSampleLineContext[K],
  ) => void;
  /** 方面の変更通知 */
  onDirectionChange: (lineIndex: number, dirIndex: number, value: string) => void;
  /** 「試聴を開始」: ダイアログを閉じて通し試聴を始める */
  onStart: () => void;
  /** 「キャンセル」: 再生せずに閉じる */
  onCancel: () => void;
}

export function PointPreviewConditionDialog(
  props: PointPreviewConditionDialogProps,
): React.JSX.Element {
  const { fields, sample, limits, onChange, onLineChange, onDirectionChange, onStart, onCancel } =
    props;

  // Esc: キャンセル (再生しない)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="プレビュー条件"
      data-testid="point-preview-condition-dialog"
      // 背景クリックでは閉じない (onClick 未設定)。
      style={{ zIndex: 1140 }}
    >
      <div className="modal modal--single-column" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 15 }}>プレビュー条件</h2>
        </div>
        <div className="modal-body modal-body--single-column">
          <div className="form-pane">
            <div className="hint">
              試聴に使うサンプル条件を指定してください。未入力の項目はコンテンツの設定から自動で補います。
            </div>
            <PreviewSamplePanel
              testIdSuffix="point"
              fields={fields}
              sample={sample}
              onChange={onChange}
              // 接続線・方面は「設定されている件数ぶん」指定できる。
              limits={limits}
              onLineChange={onLineChange}
              onDirectionChange={onDirectionChange}
            />
            <div className="hint">
              開始チャイム → コンテンツ (並び順) → 終了チャイム の順に再生します。
              条件付きコンテンツは「条件成立時のみ放送」されるものも含めて鳴らします。
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" data-testid="point-preview-condition-cancel" onClick={onCancel}>
            キャンセル
          </button>
          <button
            type="button"
            className="primary"
            data-testid="point-preview-start"
            onClick={onStart}
            autoFocus
          >
            ▶ 試聴を開始
          </button>
        </div>
      </div>
    </div>
  );
}
